import { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { Box, List, ListItemText } from '@mui/material';
import { StyledNavItem, StyledNavItemIcon, StyledSubItem } from './styles';
import Iconify from '../iconify';
import axios from '../../utils/Axios';
import palette from 'src/theme/palette';

// A child with `feature: 'calendar'` shows to every account that is not blocked, because the
// Google Calendar rollout is at `all` (ADR-17). The page itself still asks the API and answers
// `calendar_off` with the 404 page or the disconnect-only view, so the nav need not know the mode.
function isFeatureShown(child, user) {
  if (child.feature !== 'calendar') return true;
  return user?.calendarSync?.blocked !== true;
}

export default function NavSection({
  data = [],
  userId,
  user,
  isExpiredSub,
  onboardingLocked = false,
  ...other
}) {
  console.log("user", user);


  // Filter out "Set Up" menu item when onboarding is complete
  const filteredData = data
    .filter((item) => {
      if (item.path === '/dashboard/setup' && user?.onboardingCompleted === true) {
        return false;
      }
      return true;
    })
    .map((item) =>
      item.children ? { ...item, children: item.children.filter((child) => isFeatureShown(child, user)) } : item
    );

  return (
    <Box {...other}>
      <List disablePadding>
        {filteredData.map((item) => (
          <NavItem
            key={item.title}
            item={item}
            userId={userId}
            isExpiredSub={isExpiredSub}
            onboardingLocked={onboardingLocked}
          />
        ))}


      </List>
    </Box>
  );
}

function NavItem({ item, userId, isExpiredSub, onboardingLocked }) {
  const {
    title,
    path,
    icon,
    info,
    children,
    stripePortal,
    isSubOnly,
    isExternalLink,
    lockable = true,
  } = item;
  const [openMenu, setOpenMenu] = useState(false);

  const toggleMenu = () => {
    setOpenMenu(!openMenu);
  };

  // maybe move this sections layout ..
  const goToStripePortal = async () => {
    const stripePortal = window.open('', '_blank');
    try {
      stripePortal.document.write('Loading billing portal...');
      const response = await axios.post(`/users/${userId}/stripe_portal`);
      stripePortal.location.href = response.data.sessionUrl;
    } catch (err) {
      console.log(err);
      stripePortal.close();
    }
  };

  const goToSupport = () => {
    const supportPage = window.open('', '_blank');
    supportPage.document.write('Loading support page ...');
    supportPage.location.href = 'https://www.flowbookings.io/contact';
  };

  const goToOldVerstion = () => {
    const supportPage = window.open('', '_blank');
    supportPage.document.write('Loading old version ...');
    supportPage.location.href = 'https://app-old-version.flowbookings.io';
  };

  const shouldHideLink = isExpiredSub && isSubOnly;
  const isLocked = onboardingLocked && lockable;
  
  // Check if all children are locked (for parent menu styling)
  const areAllChildrenLocked = children.length > 0 && 
    children.every(child => onboardingLocked && child.lockable !== false);

  const getDestination = (targetPath, targetLockable = lockable) => {
    if (onboardingLocked && targetLockable !== false) return '/dashboard/setup';
    return targetPath;
  };

  const itemStyles = {
    mt: 0.5,
    color: isLocked ? 'text.disabled' : undefined,
    pointerEvents: isLocked ? 'auto' : undefined,
    cursor: isLocked ? 'not-allowed' : undefined,
    '&.active': {
      color: isLocked ? 'text.disabled' : palette.primary.main,
      bgcolor: isLocked ? 'transparent' : palette.primary.lighter,
      fontWeight: isLocked ? 'fontWeightRegular' : 'fontWeightBold',
    },
  };
  const subSection = (
    <>
      <StyledNavItem 
        onClick={toggleMenu}
        sx={{
          mt: 0.5,
          color: areAllChildrenLocked ? 'text.disabled' : undefined,
          cursor: areAllChildrenLocked ? 'not-allowed' : undefined,
        }}
      >
        <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
        <ListItemText disableTypography primary={title} />
        {!openMenu && (
          <Iconify
            width={16}
            icon="eva:arrow-ios-forward-fill"
            sx={{ mr: 1 }}
          />
        )}
        {openMenu && (
          <Iconify
            width={16}
            icon="eva:arrow-ios-downward-fill"
            sx={{ mr: 1 }}
          />
        )}
      </StyledNavItem>
      {openMenu &&
        children.map((child) => (
          <Box>
            <StyledSubItem
              component={RouterLink}
              to={getDestination(child.path, child.lockable)}
              sx={{
                mt: 0.5,
                color: isLocked && child.lockable !== false ? 'text.disabled' : undefined,
                cursor: isLocked && child.lockable !== false ? 'not-allowed' : undefined,
                '&.active': {
                  color:
                    isLocked && child.lockable !== false ? 'text.disabled' : palette.primary.main,
                  bgcolor:
                    isLocked && child.lockable !== false
                      ? 'transparent'
                      : palette.primary.lighter,
                  fontWeight:
                    isLocked && child.lockable !== false ? 'fontWeightRegular' : 'fontWeightBold',
                },
              }}
            >
              <StyledNavItemIcon>{child.icon}</StyledNavItemIcon>
              <ListItemText disableTypography primary={child.title} />
            </StyledSubItem>
          </Box>
        ))}
      {openMenu && stripePortal ? (
        <Box onClick={goToStripePortal}>
          <StyledSubItem
            sx={{
              mt: 0.5,
              '&.active': {
                color: palette.primary.main,
                bgcolor: palette.primary.lighter,
                fontWeight: 'fontWeightBold',
              },
            }}
          >
            <StyledNavItemIcon sx={{ width: 10, height: 10 }}>
              {stripePortal.icon}
            </StyledNavItemIcon>
            <ListItemText disableTypography primary={stripePortal.title} />
          </StyledSubItem>
        </Box>
      ) : null}
    </>
  );

  if (children.length && !shouldHideLink) {
    return subSection;
  }

  const linkRender = !isExternalLink ? (
    <StyledNavItem
      component={RouterLink}
      to={getDestination(path)}
      sx={itemStyles}
    >
      <StyledNavItemIcon>{icon && icon}</StyledNavItemIcon>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </StyledNavItem>
  ) : (
    <StyledNavItem
      onClick={goToSupport}
      sx={{
        mt: 0.5,
        '&.active': {
          color: palette.primary.main,
          bgcolor: palette.primary.lighter,
          fontWeight: 'fontWeightBold',
        },
      }}
    >
      <StyledNavItemIcon sx={{ width: 14, height: 14 }}>
        {icon && icon}
      </StyledNavItemIcon>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </StyledNavItem>
  );
  return <>{shouldHideLink ? null : linkRender}</>;
}
